import { useState } from 'react';
import type { ChangeEvent, DragEvent, FormEvent, MouseEvent, ReactNode } from 'react';

import { complianceTone } from '../format';
import { Sparkline } from './Charts';

/**
 * The panel's components.
 *
 * Written here rather than pulled from a template. Half of SMA's admin was unremoved demo
 * content — dashboards for products that did not exist, charts wired to sample data — and
 * separating the real screens from the decoration cost more than building this would have.
 *
 * Small on purpose. These are the pieces every screen actually needs; anything used once
 * belongs in the screen that uses it.
 */

export function Card({
  title,
  actions,
  children,
}: {
  title?: string | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {(title !== undefined || actions !== undefined) && (
        <header className="card-header">
          {title !== undefined && <h2>{title}</h2>}
          {actions !== undefined && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
}: {
  children: ReactNode;
  /*
   * Receives the click. Without it `stopPropagation` could not be called from a Button at
   * all, so a Button inside a clickable table row always fired the row's handler too — and
   * the only way to write the correct code was to drop to a raw <button>, which is what the
   * one place that got it right had done. A handler taking no argument still fits.
   */
  onClick?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  type?: 'button' | 'submit' | undefined;
  variant?: 'primary' | 'secondary' | 'danger' | undefined;
  disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <button className={`btn btn-${variant}`} type={type} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  hint,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'number' | 'date' | 'time' | 'datetime-local' | 'email' | undefined;
  required?: boolean | undefined;
  hint?: string | undefined;
  placeholder?: string | undefined;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {required && <em aria-hidden="true"> *</em>}
      </span>
      <input
        className="field-input"
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      />
      {hint !== undefined && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  placeholder = '—',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * `group` is optional and, where present, sorts the list under headings.
   *
   * Added for the rule editor's question picker: a flat list of every field key in the
   * deployment is unreadable once there is more than one form, and identical prompts in
   * different forms are indistinguishable without saying which form they came from.
   */
  options: Array<{ value: string; label: string; group?: string | undefined }>;
  required?: boolean | undefined;
  placeholder?: string | undefined;
}): JSX.Element {
  // Insertion-ordered, so the caller decides the order of the headings rather than having
  // it alphabetised behind their back.
  const groups = new Map<string, Array<{ value: string; label: string }>>();
  for (const option of options) {
    const name = option.group ?? '';
    groups.set(name, [...(groups.get(name) ?? []), option]);
  }
  const grouped = options.some((option) => option.group !== undefined);

  return (
    <label className="field">
      <span className="field-label">
        {label}
        {required && <em aria-hidden="true"> *</em>}
      </span>
      <select
        className="field-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {grouped
          ? [...groups.entries()].map(([name, entries]) => (
              <optgroup key={name} label={name}>
                {entries.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
      </select>
    </label>
  );
}

export function Form({
  children,
  onSubmit,
}: {
  children: ReactNode;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <form
      className="form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}

/**
 * A message the user needs to read.
 *
 * Errors are never swallowed and never replaced with a generic string — the server's own
 * message is written to be shown, and "This visit is complete and cannot be changed" is
 * more useful than any wording this layer could invent.
 */
export function Notice({
  kind,
  children,
  onDismiss,
}: {
  kind: 'error' | 'success' | 'info';
  children: ReactNode;
  onDismiss?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span>{children}</span>
      {onDismiss !== undefined && (
        <button className="notice-dismiss" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

export function Badge({ children, tone }: { children: ReactNode; tone: string }): JSX.Element {
  return <span className={`badge badge-${tone.toLowerCase()}`}>{children}</span>;
}

export interface Column<T> {
  header: string;
  /** Rendered cell. A function rather than a key path so a column can combine fields. */
  render: (row: T) => ReactNode;
  /** Right-aligns numeric columns, which are far easier to compare that way. */
  numeric?: boolean | undefined;
}

export function Table<T>({
  columns,
  rows,
  keyOf,
  empty = 'Nothing to show.',
  onRowClick,
  onReorder,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  keyOf: (row: T) => string;
  empty?: string | undefined;
  onRowClick?: ((row: T) => void) | undefined;
  /**
   * Supply this to make rows draggable. Called with the whole list in its new order.
   *
   * Drag is an addition, never the only route — it is mouse-only, so any table that offers
   * it should keep its up/down controls for everyone else.
   */
  onReorder?: ((ordered: T[]) => void) | undefined;
}): JSX.Element {
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  if (rows.length === 0) {
    // An explicit empty state, not a blank area. A table that renders nothing is
    // indistinguishable from one that failed to load.
    return <p className="empty">{empty}</p>;
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.header} className={column.numeric === true ? 'numeric' : undefined}>
                {column.header}
              </th>
            ))}
            {/* The chevron's column, so the header rule runs the full width of the table. */}
            {onRowClick !== undefined && <th className="chevron-cell" aria-hidden="true" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = keyOf(row);

            const reorderProps =
              onReorder === undefined
                ? {}
                : {
                    draggable: true,
                    onDragStart: (event: DragEvent<HTMLTableRowElement>) => {
                      setDraggingKey(key);
                      // Firefox will not start a drag with an empty transfer.
                      event.dataTransfer.setData('text/plain', key);
                      event.dataTransfer.effectAllowed = 'move' as const;
                    },
                    onDragEnd: () => {
                      setDraggingKey(null);
                      setOverKey(null);
                    },
                    onDragOver: (event: DragEvent<HTMLTableRowElement>) => {
                      if (draggingKey === null) return;
                      event.preventDefault();
                      setOverKey(key);
                    },
                    onDrop: (event: DragEvent<HTMLTableRowElement>) => {
                      event.preventDefault();
                      const fromKey = draggingKey ?? event.dataTransfer.getData('text/plain');
                      setDraggingKey(null);
                      setOverKey(null);
                      if (fromKey === '' || fromKey === key) return;

                      const from = rows.findIndex((entry) => keyOf(entry) === fromKey);
                      const to = rows.findIndex((entry) => keyOf(entry) === key);
                      if (from === -1 || to === -1) return;

                      const next = [...rows];
                      const [moved] = next.splice(from, 1);
                      if (moved === undefined) return;
                      next.splice(to, 0, moved);
                      onReorder(next);
                    },
                    style: {
                      opacity: draggingKey === key ? 0.4 : 1,
                      // A landing line, rather than reflowing rows under the cursor.
                      boxShadow:
                        overKey === key && draggingKey !== key
                          ? 'inset 0 3px 0 0 var(--accent, #1f5f8b)'
                          : undefined,
                      cursor: 'grab',
                    },
                  };

            return (
            <tr
              key={key}
              {...reorderProps}
              onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
              className={onRowClick === undefined ? undefined : 'clickable'}
            >
              {columns.map((column) => (
                <td key={column.header} className={column.numeric === true ? 'numeric' : undefined}>
                  {column.render(row)}
                </td>
              ))}
              {/*
                A row that opens something says so before it is hovered.
                
                The pointer cursor and the hover tint only appear once the mouse is already
                on the row, so a reader scanning a dense table had nothing telling them the
                rows were more than text — the detail behind each one was reachable only by
                guessing. The chevron is the resting-state cue.
              */}
              {onRowClick !== undefined && (
                <td className="chevron-cell" aria-hidden="true">
                  ›
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A percentage, written so it can be scanned rather than read.
 *
 * Compliance is the number these tables exist for, and it was rendered as plain text in the
 * last column: 0% and 100% had exactly the same weight, so finding the worst store meant
 * reading every row. The bar gives the eye something to compare lengths of, and the colour
 * says which end of the scale it is at without a legend.
 *
 * The figure is still written out. A bar alone would be a picture of a number nobody can
 * quote in a meeting.
 */
export function Percentage({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <span className="muted">—</span>;

  const clamped = Math.max(0, Math.min(100, value));
  const tone = complianceTone(clamped);

  return (
    <span className="percentage" title={`${clamped.toFixed(1)}%`}>
      <span className="percentage-track" aria-hidden="true">
        <span className={`percentage-fill percentage-${tone}`} style={{ width: `${clamped}%` }} />
      </span>
      <span className={`percentage-value percentage-text-${tone}`}>{Math.round(clamped)}%</span>
    </span>
  );
}

/**
 * A headline figure, optionally with where it came from.
 *
 * `trend` draws the months behind the number and `comparedWith` says what the last of them
 * did. The two are separate because a shape and a change answer different questions: the
 * sparkline shows whether this month is typical, the delta shows which way it moved.
 *
 * `upIsGood` decides the delta's colour, and has three states on purpose. More blocked
 * submissions is bad and more accepted ones good, but more submissions is neither — a month
 * with more visits than the last is just a busier month, and painting it green would invent
 * a judgement the number does not carry.
 */
export function Metric({
  label,
  value,
  tone,
  trend,
  comparedWith,
  upIsGood,
}: {
  label: string;
  value: number | string;
  tone?: string | undefined;
  trend?: readonly number[] | undefined;
  /**
   * `caption` names both periods rather than only the earlier one. These tiles carry
   * all-time totals, so a bare "vs 2026-07" beside them invites reading the change as a
   * change in the total; "2026-08 vs 2026-07" can only be read as the months it is.
   */
  comparedWith?: { latest: number; previous: number; caption: string } | undefined;
  upIsGood?: boolean | undefined;
}): JSX.Element {
  // Measured between the two periods named in the caption, never against `value` — that is
  // a running total, and subtracting a month from it would produce a confident wrong number.
  const change = comparedWith === undefined ? null : comparedWith.latest - comparedWith.previous;

  // No arrow and no colour for an unchanged figure — "→ 0" is noise where "no change" is
  // the whole message.
  const direction = change === null || change === 0 ? null : change > 0 ? 'up' : 'down';
  const deltaTone =
    direction === null || upIsGood === undefined
      ? 'neutral'
      : (direction === 'up') === upIsGood
        ? 'good'
        : 'bad';

  return (
    <div className={`metric${tone === undefined ? '' : ` metric-${tone}`}`}>
      <span className="metric-label">{label}</span>
      <span className="metric-value">{value}</span>

      {(trend !== undefined || change !== null) && (
        <span className="metric-foot">
          {change === null ? (
            <span className="metric-delta metric-delta-neutral">No earlier period</span>
          ) : (
            <span className={`metric-delta metric-delta-${deltaTone}`}>
              {direction === null
                ? 'Unchanged'
                : `${direction === 'up' ? '▲' : '▼'} ${Math.abs(change)}`}{' '}
              <span className="metric-delta-period">{comparedWith!.caption}</span>
            </span>
          )}
          {trend !== undefined && <Sparkline values={trend} />}
        </span>
      )}
    </div>
  );
}

export function Loading(): JSX.Element {
  return <p className="empty">Loading…</p>;
}

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <header className="modal-header">
          <h2>{title}</h2>
          <button className="notice-dismiss" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export { Reorderable } from './Reorder';
export { EvidenceImage } from './Evidence';
